// ADV-MKT-BOARD-CLARITY-ACTIONABILITY-001 — compact board legend + summary counts.
// Intentionally NOT a banner: the legend is a small toggle/popover, and the summary is
// a single compact count line. No order controls, advisory only.
import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { COLOR_LEGEND, CHIP_GLOSSARY, type BoardSummary } from '../lib/boardActionability';

const _DOT: Record<string, string> = {
  green: 'bg-emerald-600', 'green-soft': 'bg-emerald-500/70',
  orange: 'bg-amber-500', red: 'bg-rose-600', gray: 'bg-slate-300 border border-slate-400',
};

export function BoardLegend() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        type="button"
        data-testid="mp-board-legend-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="What do the colors and chips mean?"
        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border border-[var(--card-border)] text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-bg)]"
      >
        <Info size={11} /> Legend
      </button>
      {open && (
        <div
          data-testid="mp-board-legend-panel"
          role="dialog"
          className="absolute right-0 z-40 mt-1 w-80 max-w-[90vw] rounded-xl border border-[var(--card-border)] bg-[var(--card-bg)] p-3 shadow-lg text-[11px] normal-case tracking-normal text-[var(--app-text)]"
        >
          <div className="font-black uppercase tracking-widest text-[10px] text-[var(--muted-text)] mb-1.5">Colors</div>
          <ul className="space-y-1 mb-2">
            {COLOR_LEGEND.map((c) => (
              <li key={c.label} className="flex items-center gap-2">
                <span className={`inline-block h-3 w-3 rounded ${_DOT[c.tone] || 'bg-slate-300'}`} />
                <span className="font-bold">{c.label}</span>
                <span className="text-[var(--muted-text)]">— {c.meaning}</span>
              </li>
            ))}
          </ul>
          <div className="font-black uppercase tracking-widest text-[10px] text-[var(--muted-text)] mb-1.5">Chips</div>
          <ul className="space-y-1">
            {CHIP_GLOSSARY.map((g) => (
              <li key={g.chip}>
                <span className="font-bold">{g.chip}</span>
                <span className="text-[var(--muted-text)]"> — {g.meaning}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[10px] text-[var(--muted-text)]">Labels are advisory only. Adversyn never places orders on your behalf.</p>
        </div>
      )}
    </div>
  );
}

const _COUNT_TONE: Record<string, string> = {
  Actionable: 'text-emerald-700', Watchlist: 'text-emerald-600', Review: 'text-amber-600',
  Stale: 'text-slate-500', Partial: 'text-slate-500', Blocked: 'text-rose-600',
};

export function BoardSummaryCounts({ summary }: { summary: BoardSummary }) {
  const order: Array<keyof BoardSummary> = ['Actionable', 'Watchlist', 'Review', 'Stale', 'Partial', 'Blocked'];
  return (
    <div data-testid="mp-board-summary" className="flex items-center gap-x-3 gap-y-1 flex-wrap text-[10px] font-bold uppercase tracking-widest">
      {order.map((k) => (
        <span key={k} data-testid={`mp-summary-${String(k).toLowerCase()}`} className={_COUNT_TONE[k as string] || 'text-[var(--muted-text)]'}>
          {k}: <span data-testid={`mp-summary-${String(k).toLowerCase()}-count`}>{summary[k]}</span>
        </span>
      ))}
    </div>
  );
}
